import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Dropdown, Flex, MenuProps, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { isHasAccess, pathPermissions } from 'features/validateAccess';
import { accountService } from 'entities/account';
import { getDefaultRedirectPath } from 'entities/session';
import { ReactComponent as TimesheetsIcon } from 'shared/assets/icons/dynamicColor/corner_down_right_arrow_icon.svg';
import { ReactComponent as MenuKebab } from 'shared/assets/icons/menu-kebab.svg';
import { useMediaQuery } from 'shared/lib/react';
import { pathKeys } from 'shared/lib/react-router';
import { BackButton } from 'shared/ui/BackButton';
import { Breadcrumb, IBreadcrumbItem } from 'shared/ui/Breadcrumb';
import { LinkButton } from 'shared/ui/LinkButton';
import {
  getAccountNameViewPermission,
  getAccountViewTimesheetPermission,
  getLocalEditAccountPermission,
  getProjectCreatePermissions,
  GLOBAL_EDIT_ACCOUNT_PERMISSION,
} from '../../ProjectList.permissions';

import styles from './ProjectHeader.module.scss';

const ProjectHeader = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery('(min-width: 800px)');

  const { accountId = '' } = useParams();

  const canGoToAccountList = isHasAccess(pathPermissions.account.root);
  const showCreateButton = isHasAccess(getProjectCreatePermissions(accountId));
  const showEditButton =
    isHasAccess(GLOBAL_EDIT_ACCOUNT_PERMISSION) || isHasAccess(getLocalEditAccountPermission(accountId));
  const showTimesheets = isHasAccess(getAccountViewTimesheetPermission(accountId));

  const showAccountName = isHasAccess(getAccountNameViewPermission(accountId));

  const { data: account, isLoading } = useQuery(accountService.queryOptions(accountId, showAccountName));

  const breadcrumbs: IBreadcrumbItem[] = [
    {
      label: t('Account:AccountsPage.heading'),
      path: pathKeys.account.root(),
      disable: !canGoToAccountList,
    },
    {
      label: account?.name ?? ' ',
      hide: !showAccountName,
    },
  ];

  const dropdownMenuItems: MenuProps['items'] = useMemo(
    () => [
      showCreateButton
        ? {
            key: 'create-project',
            label: (
              <Link to={pathKeys.project.create({ accountId })}>{t('Project:ProjectsPage.createButton.label')}</Link>
            ),
          }
        : null,
      showCreateButton && (showTimesheets || showEditButton)
        ? {
            type: 'divider',
          }
        : null,
      showEditButton
        ? {
            key: 'edit-account',
            label: <Link to={pathKeys.account.update({ accountId })}>{t('Project:ProjectsPage.editBtn.label')}</Link>,
          }
        : null,
      showTimesheets
        ? {
            key: 'view-timesheets',
            disabled: !!account?.archivedAt,
            label: (
              <Link to={`${pathKeys.timesheets()}?accountId=${accountId}`}>
                {t('Project:ProjectsPage.timesheetsBtn.text')}
              </Link>
            ),
            icon: <TimesheetsIcon />,
          }
        : null,
    ],
    [account?.archivedAt, accountId, showCreateButton, showEditButton, showTimesheets, t],
  );

  const navigateBreadcrumbBack = (): void => {
    const breadcrumbLength = breadcrumbs.length;
    if (breadcrumbLength === 1) {
      navigate(getDefaultRedirectPath());
      return;
    }
    navigate(breadcrumbs[breadcrumbLength - 2].path as string);
  };

  return (
    <div className={styles.header}>
      <Flex justify="space-between" gap={4}>
        <Flex gap={8}>
          <div>
            <BackButton onClick={navigateBreadcrumbBack} isDisabled={isLoading} />
          </div>
          <Typography.Title level={3} className={styles.title}>
            {t('Project:ProjectsPage.heading')}
          </Typography.Title>
        </Flex>

        {isDesktop ? (
          <div className={styles.buttonsWrapper}>
            {showTimesheets ? (
              <LinkButton
                buttonProps={{
                  icon: <TimesheetsIcon />,
                  className: styles.timesheetsBtn,
                  disabled: !!account?.archivedAt,
                  loading: isLoading,
                }}
                linkProps={{ to: `${pathKeys.timesheets()}?accountId=${accountId}` }}
              >
                {t('Project:ProjectsPage.timesheetsBtn.text')}
              </LinkButton>
            ) : null}
            {showEditButton && showTimesheets && <Divider type="vertical" className={styles.divider} />}
            {showEditButton && (
              <LinkButton
                linkProps={{ to: pathKeys.account.update({ accountId }) }}
                buttonProps={{ loading: isLoading }}
              >
                {t('Project:ProjectsPage.editBtn.label')}
              </LinkButton>
            )}
            {showCreateButton && (
              <LinkButton
                linkProps={{ to: pathKeys.project.create({ accountId }) }}
                buttonProps={{ type: 'primary' }}
                data-testid="create-button"
              >
                {t('Project:ProjectsPage.createButton.label')}
              </LinkButton>
            )}
          </div>
        ) : (
          <Dropdown menu={{ items: dropdownMenuItems }} trigger={['click']}>
            <Button style={{ padding: 0, border: 'none' }}>
              <MenuKebab height={24} width={24} />
            </Button>
          </Dropdown>
        )}
      </Flex>

      <Skeleton
        active
        paragraph={{ rows: 0 }}
        title={{ width: 320 }}
        className={styles.breadcrumbSkeleton}
        loading={isLoading}
      >
        <div className={styles.breadcrumbWrapper}>
          <Breadcrumb items={breadcrumbs} />
        </div>
      </Skeleton>
    </div>
  );
};

export default ProjectHeader;
